import React, { useEffect, useState } from "react";
import { MatrixRain } from "@/components/MatrixRain";
import {
  getStreamPresets,
  saveStreamPresets,
  getTwitchStatus,
  applyTwitchPreset,
  getKickStatus,
  applyKickPreset,
  getYouTubeStatus,
  getYouTubeBroadcasts,
  applyYouTubePreset,
} from "@/lib/api";

const PLATFORMS = ["Twitch", "YouTube", "TikTok", "Facebook", "Kick"];

const PLATFORM_STYLES = {
  Twitch: {
    active: "border-purple-400 bg-purple-500/25 text-purple-200 shadow-[0_0_24px_4px_rgba(168,85,247,0.35)] ring-1 ring-purple-400/50",
  },
  YouTube: {
    active: "border-red-400 bg-red-500/25 text-red-200 shadow-[0_0_24px_4px_rgba(239,68,68,0.35)] ring-1 ring-red-400/50",
  },
  TikTok: {
    active: "border-zinc-400/60 bg-gradient-to-br from-zinc-700/70 via-zinc-900 to-black text-white shadow-[0_0_22px_4px_rgba(0,0,0,0.95)] ring-1 ring-zinc-500/40",
  },
  Facebook: {
    active: "border-blue-400 bg-blue-500/25 text-blue-200 shadow-[0_0_24px_4px_rgba(59,130,246,0.35)] ring-1 ring-blue-400/50",
  },
  Kick: {
    active: "border-lime-400 bg-lime-500/25 text-lime-300 shadow-[0_0_24px_4px_rgba(83,252,24,0.35)] ring-1 ring-lime-400/50",
  },
};

const FIELD_LABELS = {
  title: "Stream Title",
  golivenotification: "Go-Live Notification",
  description: "Description",
  category: "Category",
  tags: "Tags",
  extra: "Additional Information",
  aboutme: "About Me",
};

// Fields shown in the editor, in display order.
// Fields omitted here remain stored in existing presets.
const PLATFORM_FIELDS = {
  Twitch: ["title", "golivenotification", "category", "tags"],
  YouTube: ["title", "description", "category", "tags"],
  TikTok: ["title", "aboutme"],
  Facebook: ["title", "description"],
  Kick: ["title", "category"],
};

// Hard limits used by the Doriantelive editor.
// Some are editor-selected limits, not verified platform API limits.
const FIELD_LIMITS = {
  Twitch: {
    title: 140,
    golivenotification: 140,
  },
  YouTube: {
    title: 100,
    description: 5000,
    tags: 500,
  },
  TikTok: {
    title: 32,
    aboutme: 240,
  },
  Facebook: {
    title: 254,
    description: 5000,
  },
  Kick: {
    title: 140,
  },
};

// Twitch tags are validated individually, not as a 250-character field.
// The editor stores tags as comma-separated text.
const TWITCH_TAG_LIMIT = 10;
const TWITCH_TAG_LENGTH = 25;

const TWITCH_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "fr", name: "French" },
  { code: "es", name: "Spanish" },
  { code: "de", name: "German" },
  { code: "pt", name: "Portuguese" },
  { code: "it", name: "Italian" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "zh", name: "Chinese" },
  { code: "ru", name: "Russian" },
  { code: "ar", name: "Arabic" },
  { code: "hi", name: "Hindi" },
  { code: "nl", name: "Dutch" },
  { code: "pl", name: "Polish" },
  { code: "tr", name: "Turkish" },
];

const TWITCH_CLASSIFICATIONS = [
  {
    id: "DebatedSocialIssuesAndPolitics",
    label: "Politics and Sensitive Social Issues",
  },
  {
    id: "DrugsIntoxication",
    label: "Drugs, Alcohol, or Intoxication",
  },
  {
    id: "SexualThemes",
    label: "Sexual Themes",
  },
  {
    id: "ViolentGraphic",
    label: "Graphic Violence",
  },
  {
    id: "Gambling",
    label: "Gambling",
  },
  {
    id: "ProfanityVulgarity",
    label: "Significant Profanity or Vulgarity",
  },
];

const countCharacters = (value) => Array.from(value).length;

const trimCharacters = (value, maximum) =>
  Array.from(value).slice(0, maximum).join("");

const youtubeTagLength = (value) => {
  const tags = value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

  return tags.reduce(
    (total, tag) =>
      total + tag.length + (tag.includes(" ") ? 2 : 0),
    0
  );
};

const enforceFieldLimit = (platform, field, value) => {
  if (platform === "Twitch" && field === "tags") {
    const tags = value.split(",");

    return tags
      .slice(0, TWITCH_TAG_LIMIT)
      .map((tag) => trimCharacters(tag, TWITCH_TAG_LENGTH))
      .join(",");
  }

  if (platform === "YouTube" && field === "description") {
    const encoder = new TextEncoder();

    let result = "";

    for (const character of value) {
      if (encoder.encode(result + character).length > 5000) {
        break;
      }

      result += character;
    }

    return result;
  }

  if (platform === "YouTube" && field === "tags") {
    let result = "";

    for (const character of value) {
      const candidate = result + character;

      if (youtubeTagLength(candidate) > 500) {
        break;
      }

      result = candidate;
    }

    return result;
  }

  const limit = FIELD_LIMITS[platform]?.[field];

  return typeof limit === "number"
    ? trimCharacters(value, limit)
    : value;
};

export default function StreamPresets() {
  const [library, setLibrary] = useState({});
  const [revision, setRevision] = useState(0);
  const [game, setGame] = useState("");
  const [preset, setPreset] = useState("");
  const [platform, setPlatform] = useState("Twitch");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [twitchConnection, setTwitchConnection] = useState(null);
  const [twitchChecking, setTwitchChecking] = useState(true);
  const [twitchApplying, setTwitchApplying] = useState(false);
  const [kickConnection, setKickConnection] = useState(null);
  const [kickChecking, setKickChecking] = useState(true);
  const [kickApplying, setKickApplying] = useState(false);
  const [youtubeConnection, setYoutubeConnection] = useState(null);
  const [youtubeChecking, setYoutubeChecking] = useState(true);
  const [youtubeApplying, setYoutubeApplying] = useState(false);
  const [youtubeBroadcasts, setYoutubeBroadcasts] = useState([]);
  const [youtubeBroadcastId, setYoutubeBroadcastId] = useState("");
  const [youtubeBroadcastError, setYoutubeBroadcastError] = useState("");

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const response = await getStreamPresets();

        if (!active) return;

        const data = response.data || {};
        const firstGame = Object.keys(data)[0] || "";

        setLibrary(data);
        setRevision(response.revision);
        setGame(firstGame);
        setPreset(Object.keys(data[firstGame] || {})[0] || "");
      } catch (err) {
        if (active) {
          setError(
            err.response?.data?.detail ||
            "Unable to load streaming presets."
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function checkTwitch() {
      try {
        const result = await getTwitchStatus();
        if (active) setTwitchConnection(result);
      } catch {
        if (active) setTwitchConnection({ connected: false });
      } finally {
        if (active) setTwitchChecking(false);
      }
    }

    checkTwitch();

    return () => {
      active = false;
    };
  }, []);


  useEffect(() => {
    let active = true;

    async function checkKick() {
      try {
        const result = await getKickStatus();
        if (active) setKickConnection(result);
      } catch {
        if (active) setKickConnection({ connected: false });
      } finally {
        if (active) setKickChecking(false);
      }
    }

    checkKick();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function checkYoutube() {
      try {
        const status = await getYouTubeStatus();
        if (!active) return;
        setYoutubeConnection(status);

        if (status.connected) {
          try {
            const result = await getYouTubeBroadcasts();
            if (!active) return;
            const items = result.broadcasts || [];
            setYoutubeBroadcasts(items);
            setYoutubeBroadcastId("");
            setYoutubeBroadcastError("");
          } catch (err) {
            if (active) {
              setYoutubeBroadcastError(
                err.response?.data?.detail ||
                "Unable to load YouTube broadcasts."
              );
            }
          }
        }
      } catch {
        if (active) setYoutubeConnection({ connected: false });
      } finally {
        if (active) setYoutubeChecking(false);
      }
    }

    checkYoutube();
    return () => { active = false; };
  }, []);

  const games = Object.keys(library);
  const presets = Object.keys(library[game] || {});

  const currentPreset = library[game]?.[preset] || {};
  const platforms = currentPreset.platforms || {};

  const platformKey =
    Object.keys(platforms).find(
      (key) => key.toLowerCase() === platform.toLowerCase()
    ) || platform;

  const fields = platforms[platformKey] || {};

  const updateField = (field, value) => {
    setLibrary((previous) => {
      const updated = structuredClone(previous);

      updated[game][preset].platforms ||= {};
      updated[game][preset].platforms[platformKey] ||= {};
      updated[game][preset].platforms[platformKey][field] = value;

      return updated;
    });

    setDirty(true);
    setMessage("");
    setError("");
  };

  const handleApplyTwitch = async () => {
    if (!game || !preset || twitchApplying || saving) return;

    setMessage("");
    setError("");

    if (!twitchConnection?.connected) {
      setError("Connect your Twitch account from the Members dashboard first.");
      return;
    }

    const selectedPreset = library[game]?.[preset];
    const platformData = selectedPreset?.platforms || {};

    const twitchKey = Object.keys(platformData).find(
      (key) => key.toLowerCase() === "twitch"
    );

    const twitchFields = twitchKey ? platformData[twitchKey] : null;

    if (!twitchFields) {
      setError("This preset has no Twitch settings.");
      return;
    }

    const payload = {};

    if (typeof twitchFields.title === "string" && twitchFields.title.trim()) {
      payload.title = twitchFields.title.trim();
    }

    if (typeof twitchFields.category === "string" && twitchFields.category.trim()) {
      payload.category = twitchFields.category.trim();
    }

    if (typeof twitchFields.tags === "string" && twitchFields.tags.trim()) {
      const tags = twitchFields.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);

      if (
        tags.length > 10 ||
        tags.some(
          (tag) =>
            Array.from(tag).length > 25 ||
            !/^[\p{L}\p{N}]+$/u.test(tag)
        )
      ) {
        setError(
          "Twitch allows up to 10 tags, each with a maximum of 25 characters and no spaces or special characters."
        );
        return;
      }

      payload.tags = tags;
    }

    if (typeof twitchFields.broadcaster_language === "string" &&
        twitchFields.broadcaster_language.trim()) {
      payload.broadcaster_language =
        twitchFields.broadcaster_language.trim();
    }

    if (
      twitchFields.content_classification_labels &&
      typeof twitchFields.content_classification_labels === "object" &&
      !Array.isArray(twitchFields.content_classification_labels)
    ) {
      payload.content_classification_labels =
        twitchFields.content_classification_labels;
    }

    if (typeof twitchFields.is_branded_content === "boolean") {
      payload.is_branded_content = twitchFields.is_branded_content;
    }

    if (Object.keys(payload).length === 0) {
      setError("This preset has no Twitch settings to apply.");
      return;
    }

    if (
      !window.confirm(
        `Apply preset "${preset}" to Twitch account "${twitchConnection.twitch_display_name || twitchConnection.twitch_login || "Connected account"}"?`
      )
    ) {
      return;
    }

    setTwitchApplying(true);

    try {
      const result = await applyTwitchPreset(payload);

      if (!result.success) {
        throw new Error("Twitch did not confirm the update.");
      }

      setMessage(
        `Twitch accepted the update for "${preset}". Check your Twitch Stream Manager to verify the changes.`
      );
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : err.message || "Unable to apply the preset to Twitch."
      );
    } finally {
      setTwitchApplying(false);
    }
  };


  const handleApplyKick = async () => {
    setMessage("");
    setError("");

    if (dirty) {
      setError("Save your preset changes before applying to Kick.");
      return;
    }

    if (!kickConnection?.connected) {
      setError("Connect your Kick account in Members first.");
      return;
    }

    const current = library[game]?.[preset];
    const allPlatforms = current?.platforms || {};

    const kickKey = Object.keys(allPlatforms).find(
      (key) => key.toLowerCase() === "kick"
    );

    const kickFields = kickKey ? allPlatforms[kickKey] : null;

    if (!kickFields) {
      setError("This preset has no Kick settings.");
      return;
    }

    const payload = {};

    if (
      typeof kickFields.title === "string" &&
      kickFields.title.trim()
    ) {
      payload.title = kickFields.title.trim();
    }

    if (
      typeof kickFields.category === "string" &&
      kickFields.category.trim()
    ) {
      payload.category = kickFields.category.trim();
    }

    if (!Object.keys(payload).length) {
      setError("This preset has no Kick settings to apply.");
      return;
    }

    if (!window.confirm(
      `Apply preset "${preset}" to Kick account "${
        kickConnection.kick_slug ||
        kickConnection.kick_user_id ||
        "Connected account"
      }"?`
    )) return;

    setKickApplying(true);

    try {
      const result = await applyKickPreset(payload);

      if (!result.success) {
        throw new Error("Kick did not confirm the update.");
      }

      setMessage(
        `Kick accepted the update for "${preset}". Check your Kick channel to verify the changes.`
      );
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : err.message || "Unable to apply the preset to Kick."
      );
    } finally {
      setKickApplying(false);
    }
  };

  const handleApplyYoutube = async () => {
    setMessage("");
    setError("");

    if (dirty) {
      setError("Save your preset changes before applying to YouTube.");
      return;
    }

    if (!youtubeConnection?.connected) {
      setError("Connect your YouTube channel in Members first.");
      return;
    }

    if (!youtubeBroadcastId) {
      setError("Select a YouTube broadcast first.");
      return;
    }

    const current = library[game]?.[preset];
    const allPlatforms = current?.platforms || {};
    const youtubeKey = Object.keys(allPlatforms).find(
      (key) => key.toLowerCase() === "youtube"
    );
    const youtubeFields = youtubeKey ? allPlatforms[youtubeKey] : null;

    if (!youtubeFields) {
      setError("This preset has no YouTube settings.");
      return;
    }

    const payload = { broadcast_id: youtubeBroadcastId };

    if (typeof youtubeFields.title === "string" && youtubeFields.title.trim()) {
      payload.title = youtubeFields.title.trim();
    }
    if (typeof youtubeFields.description === "string") {
      payload.description = youtubeFields.description;
    }
    if (typeof youtubeFields.category === "string" && youtubeFields.category.trim()) {
      payload.category = youtubeFields.category.trim();
    }
    if (typeof youtubeFields.tags === "string" && youtubeFields.tags.trim()) {
      payload.tags = youtubeFields.tags.split(",").map(
        (tag) => tag.trim()
      ).filter(Boolean);
    }

    if (Object.keys(payload).length === 1) {
      setError("This preset has no YouTube settings to apply.");
      return;
    }

    const broadcast = youtubeBroadcasts.find(
      (item) => item.id === youtubeBroadcastId
    );

    if (!window.confirm(
      `Apply preset "${preset}" to YouTube broadcast "${
        broadcast?.title || youtubeBroadcastId
      }"?`
    )) return;

    setYoutubeApplying(true);
    try {
      const result = await applyYouTubePreset(payload);
      if (!result.success) {
        throw new Error("YouTube did not confirm the update.");
      }
      setMessage(
        `YouTube accepted the update for "${preset}". Check YouTube Studio to verify.`
      );
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : err.message || "Unable to apply the preset to YouTube."
      );
    } finally {
      setYoutubeApplying(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    setError("");

    try {
      const response = await saveStreamPresets({
        revision,
        data: library,
      });

      setRevision(response.revision);
      setLibrary(response.data);
      setDirty(false);
      setMessage("Streaming presets saved successfully.");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        "Unable to save streaming presets."
      );
    } finally {
      setSaving(false);
    }
  };

  const selectGame = (value) => {
    setGame(value);
    setPreset(Object.keys(library[value] || {})[0] || "");
  };

  const createBlankPreset = () => ({
    platforms: {
      Twitch: {
        title: "",
        golivenotification: "",
        category: "",
        tags: "",
      },
      YouTube: {
        title: "",
        description: "",
        category: "",
        tags: "",
      },
      TikTok: {
        title: "",
        tags: "",
        extra: "",
      },
      Facebook: {
        title: "",
        description: "",
        category: "",
        tags: "",
        extra: "",
      },
      Kick: {
        title: "",
        category: "",
        tags: "",
      },
    },
  });

  const markChanged = () => {
    setDirty(true);
    setMessage("");
    setError("");
  };

  const askName = (label, current = "") => {
    const result = window.prompt(label, current);
    return result === null ? null : result.trim();
  };

  const createGame = () => {
    const name = askName("New game or category name:");

    if (!name) return;

    if (Object.hasOwn(library, name)) {
      window.alert("That game or category already exists.");
      return;
    }

    setLibrary((previous) => ({
      ...previous,
      [name]: {},
    }));

    setGame(name);
    setPreset("");
    markChanged();
  };

  const createPreset = () => {
    if (!game) {
      window.alert("Create a game or category first.");
      return;
    }

    const name = askName("New preset name:");

    if (!name) return;

    if (Object.hasOwn(library[game] || {}, name)) {
      window.alert("That preset already exists.");
      return;
    }

    setLibrary((previous) => ({
      ...previous,
      [game]: {
        ...previous[game],
        [name]: createBlankPreset(),
      },
    }));

    setPreset(name);
    setPlatform("Twitch");
    markChanged();
  };

  const duplicatePreset = () => {
    if (!game || !preset) return;

    const name = askName("Name for duplicated preset:", `${preset} Copy`);

    if (!name) return;

    if (Object.hasOwn(library[game], name)) {
      window.alert("That preset already exists.");
      return;
    }

    setLibrary((previous) => ({
      ...previous,
      [game]: {
        ...previous[game],
        [name]: structuredClone(previous[game][preset]),
      },
    }));

    setPreset(name);
    markChanged();
  };

  const renameGame = () => {
    if (!game) return;

    const name = askName("Rename game or category:", game);

    if (!name || name === game) return;

    if (Object.hasOwn(library, name)) {
      window.alert("That game or category already exists.");
      return;
    }

    setLibrary((previous) => {
      const updated = {};

      Object.entries(previous).forEach(([key, value]) => {
        updated[key === game ? name : key] = value;
      });

      return updated;
    });

    setGame(name);
    markChanged();
  };

  const renamePreset = () => {
    if (!game || !preset) return;

    const name = askName("Rename preset:", preset);

    if (!name || name === preset) return;

    if (Object.hasOwn(library[game], name)) {
      window.alert("That preset already exists.");
      return;
    }

    setLibrary((previous) => {
      const updatedGame = {};

      Object.entries(previous[game]).forEach(([key, value]) => {
        updatedGame[key === preset ? name : key] = value;
      });

      return {
        ...previous,
        [game]: updatedGame,
      };
    });

    setPreset(name);
    markChanged();
  };

  const deletePreset = async () => {
    if (!game || !preset || saving || loading) return;

    if (
      !window.confirm(
        `Are you sure you want to delete preset "${preset}"?`
      )
    ) {
      return;
    }

    const updatedGame = { ...library[game] };
    delete updatedGame[preset];

    const updatedLibrary = {
      ...library,
      [game]: updatedGame,
    };

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const response = await saveStreamPresets({
        revision,
        data: updatedLibrary,
      });

      setLibrary(response.data);
      setRevision(response.revision);
      setPreset(
        Object.keys(response.data[game] || {})[0] || ""
      );
      setDirty(false);
      setMessage(`Preset "${preset}" permanently deleted.`);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        "Unable to delete preset. No changes were saved."
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteGame = async () => {
    if (!game || saving || loading) return;

    const count = Object.keys(library[game] || {}).length;

    if (
      !window.confirm(
        `Are you sure you want to delete "${game}" and all ${count} presets inside it?`
      )
    ) {
      return;
    }

    const deletedGame = game;
    const updatedLibrary = { ...library };
    delete updatedLibrary[deletedGame];

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const response = await saveStreamPresets({
        revision,
        data: updatedLibrary,
      });

      const nextGame = Object.keys(response.data)[0] || "";

      setLibrary(response.data);
      setRevision(response.revision);
      setGame(nextGame);
      setPreset(
        Object.keys(response.data[nextGame] || {})[0] || ""
      );
      setDirty(false);
      setMessage(`Game "${deletedGame}" permanently deleted.`);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        "Unable to delete game. No changes were saved."
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldNames =
  game && preset
    ? PLATFORM_FIELDS[platform] || []
    : [];

  const inputClass =
    "w-full rounded-lg border border-green-500/20 bg-black/60 " +
    "px-4 py-3 text-sm text-white outline-none " +
    "focus:border-green-400/60";

  if (loading) {
    return (
      <div className="min-h-screen bg-black p-10 text-green-400">
        Loading streaming presets...
      </div>
    );
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#060a0e] text-white">
      <div className="pointer-events-none fixed inset-0 opacity-20">
        <MatrixRain />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 pt-20 pb-4 lg:px-8">
{/* COMPACT COMMAND CENTER HEADER */}
<div className="relative mb-4 overflow-hidden rounded-xl border border-emerald-500/30 bg-gradient-to-r from-[#122820] via-[#0b151a] to-[#080b10] px-5 py-4 shadow-[0_0_40px_rgba(16,185,129,0.08)]">

  {/* Subtle accent lighting */}
  <div className="pointer-events-none absolute -right-16 -top-20 h-44 w-44 rounded-full bg-emerald-500/10 blur-3xl" />
  <div className="pointer-events-none absolute bottom-0 left-0 h-px w-full bg-gradient-to-r from-emerald-400 via-orange-400 to-transparent" />

  <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">

    {/* Brand and title */}
    <div className="flex items-center gap-5">

      <div className="hidden h-12 w-1 rounded-full bg-emerald-400 shadow-[0_0_16px_rgba(52,211,153,0.6)] sm:block" />

      <div>
        <div className="mb-1 flex items-center gap-2">
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />

          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400">
            DORIANTELIVE // CREATOR SYSTEMS
          </span>
        </div>

        <h1 className="text-2xl font-black uppercase leading-none tracking-tight md:text-3xl">
          STREAM <span className="bg-gradient-to-r from-emerald-400 to-orange-400 bg-clip-text text-transparent">CONTROL</span>
        </h1>
      </div>
    </div>

    {/* Compact statistics */}
    <div className="flex items-center gap-3">

      <div className="rounded-lg border border-emerald-500/20 bg-black/40 px-4 py-2 text-center">
        <p className="font-mono text-[9px] uppercase tracking-widest text-emerald-400">
          Categories
        </p>
        <p className="text-xl font-black leading-tight text-white">
          {games.length.toString().padStart(2, "0")}
        </p>
      </div>

      <div className="rounded-lg border border-orange-500/20 bg-black/40 px-4 py-2 text-center">
        <p className="font-mono text-[9px] uppercase tracking-widest text-orange-400">
          Presets
        </p>
        <p className="text-xl font-black leading-tight text-white">
          {Object.values(library)
            .reduce(
              (total, group) => total + Object.keys(group).length,
              0
            )
            .toString()
            .padStart(2, "0")}
        </p>
      </div>

      <div className="hidden rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 font-mono text-[10px] font-bold tracking-widest text-emerald-300 lg:block">
        SYSTEM ONLINE
      </div>

    </div>
  </div>
</div>

        <section className="relative overflow-hidden rounded-2xl border border-emerald-500/25 bg-[#0d151b]/95 p-5 shadow-[0_20px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl md:p-8">
            {/* GAME AND PRESET MANAGEMENT */}
            <div className="grid grid-cols-2 gap-4">

            {/* GAME / CATEGORY */}
            <div className="space-y-3">
              <label className="block">
                <span className="mb-2 block font-mono text-xs font-bold uppercase tracking-wider text-emerald-400">
                  Game / Category
                </span>

                <select
                  className={inputClass}
                  value={game}
                  onChange={(event) => selectGame(event.target.value)}
                >
                  {games.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={createGame}
                  className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-300 transition hover:bg-emerald-500/20"
                >
                  + New Game / Category
                </button>

                <button
                  type="button"
                  onClick={renameGame}
                  disabled={!game || saving || loading}
                  className="rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-xs text-slate-300 transition hover:bg-white/10 disabled:opacity-30"
                >
                  Rename
                </button>

                <button
                  type="button"
                  onClick={deleteGame}
                  disabled={!game}
                  className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-400 transition hover:bg-red-500/10 disabled:opacity-30"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* PRESET */}
            <div className="space-y-3">
              <label className="block">
                <span className="mb-2 block font-mono text-xs font-bold uppercase tracking-wider text-orange-400">
                  Preset
                </span>

                <select
                  className={inputClass}
                  value={preset}
                  onChange={(event) => setPreset(event.target.value)}
                >
                  {presets.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={createPreset}
                  disabled={!game}
                  className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-300 transition hover:bg-emerald-500/20 disabled:opacity-30"
                >
                  + New Preset
                </button>

                <button
                  type="button"
                  onClick={duplicatePreset}
                  disabled={!preset}
                  className="rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-xs text-slate-300 transition hover:bg-white/10 disabled:opacity-30"
                >
                  Duplicate
                </button>

                <button
                  type="button"
                  onClick={renamePreset}
                  disabled={!preset}
                  className="rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-xs text-slate-300 transition hover:bg-white/10 disabled:opacity-30"
                >
                  Rename
                </button>

                <button
                  type="button"
                  onClick={deletePreset}
                  disabled={!preset || saving || loading}
                  className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-400 transition hover:bg-red-500/10 disabled:opacity-30"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {PLATFORMS.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setPlatform(item)}
                className={
                  "rounded-lg border px-5 py-3 font-mono text-xs font-black uppercase tracking-wider transition-all duration-200 " +
                  (platform === item
                    ? PLATFORM_STYLES[item].active
                    : "border-white/10 bg-[#172129] text-slate-400 hover:border-white/30 hover:bg-[#22313b] hover:text-white")
                }
              >
                {item}
              </button>
            ))}
          </div>

<div className="mt-6 space-y-4">
  {fieldNames.length === 0 ? (
    <p className="text-sm text-slate-400">
      Select or create a preset to begin editing.
    </p>
  ) : (
    fieldNames.map((field) => {
      const twitchHandle =
        twitchConnection?.twitch_login ||
        twitchConnection?.twitch_display_name ||
        "your_handle";

      const defaultGoLiveNotification = `${twitchHandle} went live!`;

      const value =
        platform === "Twitch" &&
        field === "golivenotification" &&
        !fields[field]
          ? defaultGoLiveNotification
          : typeof fields[field] === "string"
          ? fields[field]
          : fields[field] == null
          ? ""
          : JSON.stringify(fields[field]);

      const limit = FIELD_LIMITS[platform]?.[field];

      const isTwitchTags =
        platform === "Twitch" && field === "tags";

      const isYouTubeTags =
        platform === "YouTube" && field === "tags";

      const isYouTubeDescription =
        platform === "YouTube" && field === "description";

      const length = isYouTubeTags
        ? youtubeTagLength(value)
        : isYouTubeDescription
        ? new TextEncoder().encode(value).length
        : countCharacters(value);

      const tags = isTwitchTags
        ? value.split(",").filter((tag) => tag.trim())
        : [];

      const currentTag = isTwitchTags
        ? value.split(",").at(-1) || ""
        : "";

      const isFull =
        typeof limit === "number" && length >= limit;

      const nearLimit =
        typeof limit === "number" &&
        length >= limit * 0.9;

      return (
        <label key={`${platform}-${field}`} className="block">
<div className="mb-2">
  <span className="font-mono text-xs font-bold uppercase tracking-wider text-emerald-400">
    {FIELD_LABELS[field] || field}
  </span>
</div>

<div className="relative">

  <textarea
    className={
      inputClass +
      " resize-y pr-32 transition-colors " +
      (isFull
        ? "border-red-500/50"
        : "focus:border-emerald-400/70")
    }
    rows={
      ["description", "tags", "extra"].includes(field)
        ? 3
        : 2
    }
    value={value}
    onChange={(event) => {
      const nextValue = enforceFieldLimit(
        platform,
        field,
        event.target.value
      );

      updateField(field, nextValue);
    }}
    placeholder={
      isTwitchTags
        ? "Enter tags separated by commas..."
        : `Enter ${FIELD_LABELS[field] || field}...`
    }
  />

  {/* CHARACTER COUNTER INSIDE TEXT FIELD */}
  {(isTwitchTags || typeof limit === "number") && (
    <div className="pointer-events-none absolute right-1.5 top-1.5 flex items-center gap-1 font-mono text-[9px] font-normal leading-none text-white/75 tabular-nums">

      {isTwitchTags ? (
        <>
          <span
            className="text-white/75"
          >
            {tags.length}/{TWITCH_TAG_LIMIT} tags
          </span>

          <span className="text-white/40">|</span>

          <span
            className="text-white/75"
          >
            {countCharacters(currentTag)}/{TWITCH_TAG_LENGTH}
          </span>
        </>
      ) : (
        <span
          className="text-white/75"
        >
          {length}/{limit}
        </span>
      )}

    </div>
  )}

</div>

          {platform === "Twitch" &&
            field === "golivenotification" && (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled={!value.trim()}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(value);
                      setMessage("Go Live Notification copied to clipboard.");
                      setError("");
                    } catch {
                      setError("Unable to copy notification. Select and copy the text manually.");
                    }
                  }}
                  className="rounded-lg border border-purple-400/40 bg-purple-500/10 px-3 py-2 text-xs font-semibold text-purple-200 hover:bg-purple-500/20 disabled:opacity-40"
                >
                  Copy Notification
                </button>

                <a
                  href="https://dashboard.twitch.tv/settings/channel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-purple-300 underline hover:text-purple-200"
                >
                  Open Twitch Channel Settings
                </a>

                <p className="w-full text-xs text-slate-400">
                  Default: {defaultGoLiveNotification}
                  <span className="mt-1 block text-amber-400/80">
                    Twitch does not currently support automatic Go Live
                    Notification updates. To use a custom message,
                    copy it and paste it into your Twitch channel settings.
                  </span>
                </p>
              </div>
            )}

          {isTwitchTags && (
            <p className="mt-1 text-xs text-slate-500">
              Maximum 10 tags, 25 characters per tag.
              Separate tags with commas.
            </p>
          )}

          {isYouTubeTags && (
            <p className="mt-1 text-xs text-slate-500">
              YouTube tag budget: 500 characters including
              additional counting for multiword tags.
            </p>
          )}

          {isYouTubeDescription && (
            <p className="mt-1 text-xs text-slate-500">
              Counter measures UTF-8 bytes.
            </p>
          )}
        </label>
      );
    })
  )}
</div>

{platform === "Twitch" && game && preset && (
  <div className="mt-8 space-y-6 border-t border-purple-500/20 pt-6">

    {/* STREAM LANGUAGE */}
    <div>
      <label
        htmlFor="twitch-stream-language"
        className="mb-2 block font-mono text-xs font-bold uppercase tracking-wider text-purple-300"
      >
        Stream Language
      </label>

      <select
        id="twitch-stream-language"
        className={inputClass}
        value={fields.broadcaster_language || ""}
        onChange={(event) =>
          updateField("broadcaster_language", event.target.value)
        }
      >
        <option value="">Do not change Twitch language</option>

        {TWITCH_LANGUAGES.map((language) => (
          <option key={language.code} value={language.code}>
            {language.name}
          </option>
        ))}
      </select>
    </div>

    {/* CONTENT CLASSIFICATION */}
    <div>
      <h3 className="mb-2 font-mono text-xs font-bold uppercase tracking-wider text-purple-300">
        Content Classification
      </h3>

      <p className="mb-4 text-xs text-slate-400">
        Enable every classification that applies to your stream.
        When configured, applying this preset will enable selected
        labels and disable unselected labels.
      </p>

      <div className="grid gap-3 md:grid-cols-2">
        {TWITCH_CLASSIFICATIONS.map((classification) => {
          const configured =
            fields.content_classification_labels || {};

          const checked =
            configured[classification.id] === true;

          return (
            <label
              key={classification.id}
              className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 bg-black/30 px-4 py-3"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(event) => {
                  const updated = {
                    ...configured,
                    [classification.id]: event.target.checked,
                  };

                  updateField(
                    "content_classification_labels",
                    updated
                  );
                }}
                className="h-4 w-4 accent-purple-500"
              />

              <span className="text-sm text-slate-200">
                {classification.label}
              </span>
            </label>
          );
        })}
      </div>

      <p className="mt-3 text-xs text-slate-500">
        These disclosures must accurately describe your content.
        Twitch may apply certain additional labels automatically.
      </p>
    </div>

    {/* BRANDED CONTENT */}
    <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4">

      <label
        htmlFor="twitch-branded-content"
        className="mb-2 block font-mono text-xs font-bold uppercase tracking-wider text-purple-200"
      >
        Branded Content
      </label>

      <select
        id="twitch-branded-content"
        className={inputClass}
        value={
          typeof fields.is_branded_content === "boolean"
            ? String(fields.is_branded_content)
            : "unchanged"
        }
        onChange={(event) => {
          const value = event.target.value;

          updateField(
            "is_branded_content",
            value === "unchanged" ? null : value === "true"
          );
        }}
      >
        <option value="unchanged">
          Not Configured — Leave Twitch Unchanged
        </option>

        <option value="false">
          Off — Disable Branded Content
        </option>

        <option value="true">
          On — Enable Branded Content
        </option>
      </select>

      <p className="mt-3 text-xs text-slate-400">
        Enable branded content when your stream includes paid
        sponsorships, endorsements, or other branded promotions.
      </p>

      <p className="mt-2 text-xs text-purple-300/70">
        {fields.is_branded_content === true
          ? "Applying this preset will enable Twitch's branded-content disclosure."
          : fields.is_branded_content === false
          ? "Applying this preset will disable Twitch's branded-content disclosure."
          : "Applying this preset will leave Twitch's existing branded-content setting unchanged."}
      </p>
    </div>
  </div>
)}

          {error && (
            <p className="mt-6 text-sm text-red-400">{error}</p>
          )}

          {message && (
            <p className="mt-6 text-sm text-green-400">{message}</p>
          )}

          <div className="mt-8 flex items-center justify-between gap-4 border-t border-white/10 pt-6">
            <span className="text-xs text-gray-500">
              Revision {revision}
              {dirty ? " • Unsaved changes" : " • Up to date"}
            </span>


            {platform === "YouTube" && (
              <div className="flex min-w-[240px] flex-col gap-2">
                <select
                  value={youtubeBroadcastId}
                  onChange={(event) => setYoutubeBroadcastId(event.target.value)}
                  disabled={youtubeChecking || !youtubeConnection?.connected}
                  className="rounded-lg border border-red-400/40 bg-black px-3 py-3 text-sm text-white disabled:opacity-40"
                >
                  <option value="">Select YouTube broadcast...</option>
                  {youtubeBroadcasts.map((broadcast) => (
                    <option key={broadcast.id} value={broadcast.id}>
                      {broadcast.status === "active" ? "[LIVE] " : "[UPCOMING] "}
                      {broadcast.title}
                    </option>
                  ))}
                </select>
                {youtubeBroadcastError && (
                  <p className="text-xs text-red-400">{youtubeBroadcastError}</p>
                )}
                <button
                  type="button"
                  onClick={handleApplyYoutube}
                  disabled={
                    !game ||
                    !preset ||
                    dirty ||
                    saving ||
                    youtubeApplying ||
                    youtubeChecking ||
                    !youtubeConnection?.connected ||
                    !youtubeBroadcastId
                  }
                  className="rounded-lg border border-red-400 bg-red-500/20 px-6 py-3 text-sm font-semibold text-red-200 transition hover:bg-red-500/30 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {youtubeApplying
                    ? "Applying to YouTube..."
                    : youtubeChecking
                    ? "Checking YouTube..."
                    : !youtubeConnection?.connected
                    ? "YouTube Not Connected"
                    : "Apply to YouTube"}
                </button>
              </div>
            )}

            {platform === "Kick" && (
              <button
                type="button"
                onClick={handleApplyKick}
                disabled={
                  !game ||
                  !preset ||
                  dirty ||
                  saving ||
                  kickApplying ||
                  kickChecking ||
                  !kickConnection?.connected
                }
                className="rounded-lg border border-lime-400 bg-lime-500/20 px-6 py-3 text-sm font-semibold text-lime-200 transition hover:bg-lime-500/30 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {kickApplying
                  ? "Applying to Kick..."
                  : kickChecking
                  ? "Checking Kick..."
                  : kickConnection?.connected
                  ? "Apply to Kick"
                  : "Kick Not Connected"}
              </button>
            )}

            {platform === "Twitch" && (
              <button
                type="button"
                onClick={handleApplyTwitch}
                disabled={
                  !game ||
                  !preset ||
                  saving ||
                  twitchApplying ||
                  twitchChecking ||
                  !twitchConnection?.connected
                }
                className="rounded-lg border border-purple-400 bg-purple-500/20 px-6 py-3 text-sm font-semibold text-purple-200 transition hover:bg-purple-500/30 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {twitchApplying
                  ? "Applying to Twitch..."
                  : twitchChecking
                  ? "Checking Twitch..."
                  : twitchConnection?.connected
                  ? "Apply to Twitch"
                  : "Twitch Not Connected"}
              </button>
            )}

            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving}
              className="rounded-lg border border-green-400 bg-green-500/20 px-6 py-3 text-sm font-semibold text-green-300 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {saving ? "Saving..." : "Save Presets"}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}

